export type FactorConfidence =
  | "full"
  | "partial"
  | "inferred"
  | "unavailable";

export type FactorRole =
  | "quality"
  | "modifier"
  | "structural"
  | "suitability"
  | "safety";

export type FactorAssessment =
  | "ideal"
  | "helping"
  | "neutral"
  | "limiting"
  | "too weak"
  | "too strong"
  | "unavailable";

export type FactorInfluence =
  | "main limiter"
  | "secondary limiter"
  | "strong positive"
  | "minor effect"
  | "neutral"
  | "not scored";

export type FactorProvenance = {
  source:
    | "forecast"
    | "local-configuration"
    | "fallback-configuration"
    | "missing";
  provider?: string;
  fields: readonly string[];
  missingFields?: readonly string[];
};

export interface FactorExplanation {
  readonly key: string;
  readonly label: string;
  readonly reading: number | string | null;
  readonly unit: string | null;
  readonly multiplier: number | null;
  /** Actual marginal multiplier after score-side floors or powers. */
  readonly effectiveMultiplier: number | null;
  /** Exact weighted points for additive composites, otherwise null/omitted. */
  readonly contribution?: number | null;
  readonly inputs?: Readonly<Record<string, number | string | null>>;
  readonly assessment: FactorAssessment;
  readonly influence: FactorInfluence;
  readonly role: FactorRole;
  readonly why: string;
  readonly confidence: FactorConfidence;
  readonly provenance?: FactorProvenance;
}

export type SafetyExplanationSeverity =
  | "red"
  | "amber"
  | "unavailable"
  | "unknown";

export type LegacySafetyExplanationSeverity = Exclude<
  SafetyExplanationSeverity,
  "unavailable"
>;

export interface SafetyFactorExplanation<
  TSeverity extends SafetyExplanationSeverity = LegacySafetyExplanationSeverity,
> extends FactorExplanation {
  readonly role: "safety";
  readonly influence: "not scored";
  readonly multiplier: null;
  readonly effectiveMultiplier: null;
  /** Safety provenance remains the established string under `inputs`. */
  readonly provenance?: never;
  /** Canonical hazard severity from the producer. Unknown is never treated as clear. */
  readonly severity?: TSeverity;
}

export function canonicalSafetySeverity(
  value: string | null | undefined,
): "red" | "amber" | "unknown" {
  const level = value?.toLowerCase().trim();
  if (level === "red" || level === "danger") return "red";
  if (["amber", "yellow", "caution", "warning"].includes(level ?? "")) return "amber";
  return "unknown";
}

export interface ScoreExplanation<
  TSafety extends SafetyFactorExplanation<SafetyExplanationSeverity> =
    SafetyFactorExplanation<SafetyExplanationSeverity>,
> {
  readonly score: number | null;
  readonly label: string;
  readonly factors: readonly FactorExplanation[];
  readonly safety: readonly TSafety[];
  readonly confidence: FactorConfidence;
}

export interface ExplainedHourlyScore {
  readonly hour: number;
  readonly score: number;
  readonly isDay: boolean;
  readonly label: string;
}

export interface ScoreExplanationWindow {
  readonly startHour: number;
  readonly endHour: number;
}

export interface SportScoreExplanation<
  TSafety extends SafetyFactorExplanation<SafetyExplanationSeverity> =
    SafetyFactorExplanation<SafetyExplanationSeverity>,
> extends ScoreExplanation<TSafety> {
  readonly hourlyScores: readonly ExplainedHourlyScore[];
  readonly bestWindow: ScoreExplanationWindow | null;
}

/** Shared bounded wording for score factors after any averaging or flooring. */
export function assessmentFromMultiplier(
  multiplier: number | null,
  reading: number | string | null,
): FactorAssessment {
  if (reading === null) return "unavailable";
  if (multiplier === null) return "neutral";
  if (multiplier >= 0.98) return "ideal";
  if (multiplier >= 0.9) return "helping";
  if (multiplier >= 0.8) return "neutral";
  return "limiting";
}

export type ExplanationDay = "yesterday" | "today" | "tomorrow";

export function scoreExplanationForDay<
  TExplanation extends ScoreExplanation<
    SafetyFactorExplanation<SafetyExplanationSeverity>
  >,
>(
  day: ExplanationDay,
  explanations: Readonly<Record<string, TExplanation>>,
  sport: string,
): TExplanation | null {
  if (day !== "today") return null;
  return explanations[sport] ?? null;
}

export type FoilProfileKey = "prone" | "downwind" | "wing";

export interface FoilFactorExplanation extends FactorExplanation {
  readonly profile: FoilProfileKey;
}

export interface FoilProfileIdentity {
  readonly profile: FoilProfileKey;
  readonly label: string;
  readonly why: string;
}

const INFLUENCE_RANK: Readonly<Record<FactorInfluence, number>> = Object.freeze({
  "main limiter": 0,
  "secondary limiter": 1,
  "strong positive": 2,
  "minor effect": 3,
  neutral: 4,
  "not scored": 5,
});

const FOIL_PROFILE_IDENTITIES: Readonly<
  Record<FoilProfileKey, FoilProfileIdentity>
> = Object.freeze({
  prone: Object.freeze({
    profile: "prone",
    label: "Wave energy",
    why: "Prone foiling needs a catchable wave with enough clean energy to lift onto foil.",
  }),
  downwind: Object.freeze({
    profile: "downwind",
    label: "Bump energy",
    why: "Downwind foiling depends on linked wind swell that can sustain flight between bumps.",
  }),
  wing: Object.freeze({
    profile: "wing",
    label: "Wing power",
    why: "Wing foiling needs steady usable wind to create power through the hand-held wing.",
  }),
});

function normaliseFactorExplanation(
  factor: FactorExplanation,
): FactorExplanation {
  if (factor.reading === null) {
    return Object.freeze({
      ...factor,
      multiplier: null,
      effectiveMultiplier: null,
      assessment: "unavailable",
      influence: "not scored",
      confidence: "unavailable",
    });
  }

  if (factor.role === "safety") {
    return Object.freeze({ ...factor, multiplier: null, effectiveMultiplier: null, influence: "not scored" });
  }

  if (factor.role === "suitability") {
    return Object.freeze({ ...factor, multiplier: null, effectiveMultiplier: null });
  }

  return Object.freeze({ ...factor });
}

export function rankFactorExplanationsByImpact(
  factors: readonly FactorExplanation[],
): readonly FactorExplanation[] {
  const ranked = factors
    .map((factor, index) => ({ factor, index }))
    .filter(({ factor }) => factor.role !== "safety" && factor.role !== "suitability" && factor.effectiveMultiplier !== null)
    .sort((left, right) =>
      (left.factor.effectiveMultiplier ?? 1) - (right.factor.effectiveMultiplier ?? 1) ||
      left.index - right.index,
    );
  const rankByIndex = new Map(ranked.map((item, rank) => [item.index, rank]));

  return orderFactorExplanations(factors.map((factor, index) => {
    if (factor.role === "safety" || factor.role === "suitability" || factor.effectiveMultiplier === null) {
      return { ...factor, influence: "not scored" };
    }
    const rank = rankByIndex.get(index);
    const influence: FactorInfluence = factor.effectiveMultiplier >= 0.98
      ? "strong positive"
      : rank === 0
        ? "main limiter"
        : rank === 1
          ? "secondary limiter"
          : "minor effect";
    return { ...factor, influence };
  }));
}

export function orderFactorExplanations(
  factors: readonly FactorExplanation[],
): readonly FactorExplanation[] {
  const qualityRows: Array<{ factor: FactorExplanation; sourceIndex: number }> = [];
  const safetyRows: FactorExplanation[] = [];

  factors.forEach((factor, sourceIndex) => {
    const normalised = normaliseFactorExplanation(factor);

    if (normalised.role === "safety") {
      safetyRows.push(normalised);
      return;
    }

    qualityRows.push({ factor: normalised, sourceIndex });
  });

  qualityRows.sort(
    (left, right) =>
      INFLUENCE_RANK[left.factor.influence] -
        INFLUENCE_RANK[right.factor.influence] ||
      left.sourceIndex - right.sourceIndex,
  );

  return Object.freeze([
    ...qualityRows.map(({ factor }) => factor),
    ...safetyRows,
  ]);
}

export function foilProfileIdentity(
  profile: FoilProfileKey,
): FoilProfileIdentity {
  return FOIL_PROFILE_IDENTITIES[profile];
}
